
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveUserProfile, getUserProfile } from '../services/database';
import { APP_NAME } from '../constants';
import { Button } from '../components/Button';
import { FitnessLevel, UserProfile } from '../types';

export const ProfileSetup: React.FC = () => {
  const { user, setProfileLocal } = useAuth();
  const navigate = useNavigate();
  
  // Form State
  const [nickname, setNickname] = useState(user?.displayName || '');
  const [age, setAge] = useState('');
  const [country, setCountry] = useState('');
  const [dob, setDob] = useState('');
  const [level, setLevel] = useState<FitnessLevel>(FitnessLevel.BEGINNER);
  
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nickname.trim() || !user) {
      setError('Nickname is required');
      return;
    }
    if (!age || !country || !dob) {
        setError('All fields are required to complete your personnel file.');
        return;
    }

    setIsSaving(true);
    setError('');
    
    try {
      const newProfile: UserProfile = {
        uid: user.uid,
        nickname: nickname.trim(),
        email: user.email || undefined,
        age: parseInt(age),
        country: country.trim(),
        dob: dob,
        image_URL: user.photoURL || undefined, // Matches the specific DB request
        photoURL: user.photoURL || undefined,   // App internal use
        fitnessLevel: level,
        createdAt: Date.now()
      };

      // 1. Fire database save
      await saveUserProfile(user.uid, newProfile);
      
      // 2. VERIFICATION: Attempt to read it back immediately
      // This ensures the write was successful before we navigate away.
      const savedData = await getUserProfile(user.uid);
      
      if (!savedData) {
        throw new Error("Verification Failed: Data was written but could not be retrieved. Check your network connection.");
      }
      
      // 3. Optimistic Update: Update UI Context
      setProfileLocal(newProfile);

      // 4. Navigation
      navigate('/'); 
    } catch (err: any) {
      console.error("Profile save error:", err);
      
      let msg = err.message || "Database Error: Failed to save profile.";
      
      if (msg.toLowerCase().includes("permission-denied") || msg.toLowerCase().includes("missing or insufficient permissions")) {
         msg = "PERMISSION DENIED: The database rejected your data. Please ensure the Security Rules in Firebase Console are set to 'allow read, write: if request.auth != null'.";
      }
      
      setError(msg);
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 relative">
      <div className="absolute inset-0 bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAADCAYAAABS3WWCAAAAE0lEQVQIW2NkQAKrVq36zwjjAwAoOQcLlp3fzgAAAABJRU5ErkJggg==')] opacity-20 pointer-events-none mix-blend-overlay"></div>
      
      <div className="w-full max-w-md bg-surface border border-white/10 p-8 rounded-sm relative overflow-hidden shadow-2xl">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-black text-white uppercase tracking-tighter mb-2">{APP_NAME} ID</h1>
          <p className="text-secondary font-mono text-xs uppercase tracking-widest">Complete Personnel File</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Name */}
          <div>
            <label className="block text-[10px] font-mono text-slate-400 uppercase mb-1">Full Name / Codename</label>
            <input 
              type="text" 
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full bg-black border border-white/20 text-white p-3 text-sm font-bold focus:border-white focus:outline-none uppercase tracking-wider"
              placeholder="JHON DOE"
              maxLength={25}
            />
          </div>

          {/* Age & Country Row */}
          <div className="grid grid-cols-2 gap-4">
             <div>
                <label className="block text-[10px] font-mono text-slate-400 uppercase mb-1">Age</label>
                <input 
                  type="number" 
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full bg-black border border-white/20 text-white p-3 text-sm font-bold focus:border-white focus:outline-none font-mono"
                  placeholder="31"
                  min="13"
                  max="99"
                />
             </div>
             <div>
                <label className="block text-[10px] font-mono text-slate-400 uppercase mb-1">Country</label>
                <input 
                  type="text" 
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-black border border-white/20 text-white p-3 text-sm font-bold focus:border-white focus:outline-none uppercase"
                  placeholder="GERMANY"
                />
             </div>
          </div>

          {/* DOB */}
          <div>
            <label className="block text-[10px] font-mono text-slate-400 uppercase mb-1">Date of Birth</label>
            <input 
              type="date" 
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full bg-black border border-white/20 text-white p-3 text-sm font-bold focus:border-white focus:outline-none font-mono uppercase"
            />
          </div>

          {/* Fitness Level */}
          <div className="pt-2">
            <label className="block text-[10px] font-mono text-slate-400 uppercase mb-2">Current Fitness Level</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(FitnessLevel) as FitnessLevel[]).map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => setLevel(lvl)}
                  className={`p-3 text-[10px] font-bold uppercase border transition-all ${
                    level === lvl 
                      ? 'bg-white text-black border-white' 
                      : 'bg-black text-slate-400 border-white/20 hover:border-white'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-500/50 rounded-sm">
                <p className="text-red-400 text-xs font-mono text-center font-bold uppercase">{error}</p>
            </div>
          )}

          <Button 
            fullWidth 
            disabled={isSaving} 
            className="h-12 mt-4"
          >
            {isSaving ? 'SAVING TO DATABASE...' : 'CONFIRM & SAVE'}
          </Button>
        </form>
      </div>
    </div>
  );
};
